import './custom-event';

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { unix } from 'dayjs';
import { stringify } from 'qs';
import {
  refreshAccessTokenFn,
  type DefaultQueryError,
  type RefreshTokenResponse,
} from '../api';
import { apiRoutes } from '../api-routes';
import {
  API_FETCH_TIMEOUT,
  apiUrl,
  COOKIE_NAME__ACCESS_TOKEN,
  COOKIE_NAME__EXPIRES_MAX_AGE__3M,
  COOKIE_NAME__REFRESH_TOKEN,
  ENABLE_JWT_REFRESH_TOKEN,
  ErrorCode,
  JWT_REFRESH_BEFORE_EXPIRE,
  RAISE_LABEL,
  REQUIRE_AUTH_EVENT,
  USER_ROLES_NOT_CHECK_EXP,
  type UserRole,
} from '../constants';
import { type RawSetCookieOptions, getCookie, setCookie } from './cookies';
import { decodeToken } from './decode-token';

export const publicHttpClient = axios.create({
  baseURL: apiUrl,
  timeout: API_FETCH_TIMEOUT * 1000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export type ApiConfig = {
  apiURL: string;
  apiKey: string;
};

export const epHttpClient = ({ apiURL: baseURL, apiKey: apikey }: ApiConfig) =>
  axios.create({
    baseURL,
    timeout: API_FETCH_TIMEOUT * 1000,
    headers: {
      'Content-Type': 'application/json',
      apikey,
    },
  });

const httpClient = axios.create({
  baseURL: apiUrl,
  timeout: API_FETCH_TIMEOUT * 1000,
}) as HttpClientInstance;

httpClient.defaults.headers.common['Content-Type'] = 'application/json';

// eslint-disable-next-line import/no-named-as-default-member
httpClient.CancelToken = axios.CancelToken;
httpClient.isCancel = axios.isCancel;

export type HttpClientInstance = AxiosInstance & {
  CancelToken: typeof axios.CancelToken;
  isCancel: typeof axios.isCancel;
};

export type HttpClientConfig = InternalAxiosRequestConfig & {
  token_required?: boolean;
  rawConfig?: HttpClientConfig;
  stringify_data?: boolean;
  _retry?: boolean;
};

let isRefreshing = false;
let refreshPromise: Promise<string | undefined> | null = null;
let queuedRequests: {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}[] = [];

export const isTokenNotFound = (response: DefaultQueryError['response']) => {
  if (
    response &&
    ((response.data &&
      response.data.code === ErrorCode.TokenNotFound.toString()) ||
      (response.status === 404 && response?.data.message === 'TokenNotFound'))
  ) {
    return true;
  }

  return false;
};

export const isTokenExpired = (response: DefaultQueryError['response']) => {
  if (response) {
    const dataCODE = response.data?.code;

    if (
      ([ErrorCode.NotAuthorized, ErrorCode.TokenExpired] as string[]).includes(
        dataCODE
      ) ||
      response.data.message === 'TokenUnAuthorized'
    ) {
      return true;
    }
  }

  return false;
};

export const useOnFulfilledRequest = async (config: HttpClientConfig) => {
  config.rawConfig = JSON.parse(JSON.stringify(config)) as HttpClientConfig;

  const [token] = await getJWTToken(ENABLE_JWT_REFRESH_TOKEN);

  // if (!token && config.withCredentials) {
  if (!token) {
    // Cancel request if cannot get token
    dispatchRequireAuth();

    return {
      ...config,
      // eslint-disable-next-line import/no-named-as-default-member
      cancelToken: new axios.CancelToken((cancel) =>
        cancel(RAISE_LABEL.USER_NOT_LOGIN_IN)
      ),
    } as HttpClientConfig;
  }

  return updateAxiosConfig(config, token);
};

export const useOnRejectedResponse = async (error: DefaultQueryError) => {
  const { config, response } = error;
  const originalRequest = config as HttpClientConfig;

  if (
    ENABLE_JWT_REFRESH_TOKEN &&
    originalRequest &&
    !originalRequest?.url?.includes(apiRoutes.user.refreshToken) &&
    !originalRequest?._retry &&
    isTokenExpired(response)
  ) {
    // Add flag to avoid infinite loops
    originalRequest._retry = true;

    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshToken(
        (originalRequest?.headers?.Authorization as string | undefined) ?? ''
      );
    }

    try {
      const token = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      if (token) {
        queuedRequests.forEach(({ resolve }) => resolve(token));
        queuedRequests = [];
      } else {
        dispatchRequireAuth(true);
      }
    } catch (e) {
      isRefreshing = false;
      refreshPromise = null;
      queuedRequests.forEach(({ reject }) => reject(e));
      queuedRequests = [];
      dispatchRequireAuth(true);
      return Promise.reject(e);
    }

    return new Promise((resolve, reject) => {
      queuedRequests.push({
        resolve: (token: string) => {
          const modifiedOriginalRequest = updateAxiosConfig(
            originalRequest.rawConfig!,
            token
          );
          resolve(axios(modifiedOriginalRequest));
        },
        reject,
      });
    });
  } else if (isTokenNotFound(response) || isTokenExpired(response)) {
    dispatchRequireAuth(true);
  }

  // Normal flow: return error to caller
  return Promise.reject(error);
};

export const installAxiosInterceptors = (instance: HttpClientInstance) => {
  // Interceptor request
  instance.interceptors.request.use(
    async (config) => await useOnFulfilledRequest(config),
    (error) => Promise.reject(error)
  );

  // Interceptor response
  instance.interceptors.response.use(
    (res) => res,
    async (error: DefaultQueryError) => await useOnRejectedResponse(error)
  );
};

export function updateAxiosConfig(config: HttpClientConfig, token: string) {
  config = { ...config };

  if (token) {
    // const method = config.method?.toLowerCase();

    // add token to header authorization
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;

    // add token
    // if (config.token_required) {
    //   switch (method) {
    //     case 'get':
    //     case 'delete': {
    //       const params: any = config.params || {};

    //       params.token = token;
    //       config.params = params;

    //       break;
    //     }
    //     case 'post':
    //     case 'put': {
    //       const data = config.data || {};

    //       data.token = token;
    //       config.data = data;

    //       break;
    //     }
    //     default:
    //   }
    // }
  }

  if (config.stringify_data && config.data) {
    config.data = stringify(config.data);
  }

  return config;
}

export const getJWTToken = async (
  shouldCheckIsExpiredToken: boolean
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<[string, boolean]> => {
  let token = getCookie(COOKIE_NAME__ACCESS_TOKEN);

  const isNewToken = false;

  if (token && shouldCheckIsExpiredToken) {
    try {
      const decodedToken = decodeToken(String(token));

      if (decodedToken) {
        if (USER_ROLES_NOT_CHECK_EXP.includes(decodedToken.role as UserRole)) {
          return [String(token), isNewToken];
        }

        if (
          decodedToken?.exp &&
          new Date().getTime() / 1000 >
            decodedToken.exp - JWT_REFRESH_BEFORE_EXPIRE
        ) {
          // Return the current refresh promise if already refreshing
          if (isRefreshing && refreshPromise) {
            const newToken = (await refreshPromise) ?? '';
            return [newToken, true];
          }

          // Refresh token here if it's about to expire
          refreshPromise = refreshToken(String(token));
          isRefreshing = true;
          const newToken = (await refreshPromise) ?? '';
          isRefreshing = false;
          refreshPromise = null;
          return [newToken, true];
        }
      } else {
        token = '';
      }
    } catch (e) {
      console.error(e);
      token = '';
    }
  }

  return [String(token), isNewToken];
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const refreshToken = async (bearerToken: string) => {
  const decodedToken = decodeToken(bearerToken.replace('Bearer ', ''));
  const refreshKey = getCookie(COOKIE_NAME__REFRESH_TOKEN);

  if (!decodedToken || !refreshKey) return;

  let response: RefreshTokenResponse | undefined;
  try {
    response = await refreshAccessTokenFn({
      refresh_token: String(refreshKey),
      mobile: decodedToken.cid,
      type: decodedToken.type,
      imei: decodedToken.imei,
    });
  } catch (e) {
    dispatchRequireAuth(true);
    window.location.reload();
    return;
  }

  if (response?.token) {
    const decoded = decodeToken(response.token);

    const options: RawSetCookieOptions = {};

    if (decoded?.exp) {
      options.expires = unix(decoded.exp).toDate();
    } else {
      options.maxAge = COOKIE_NAME__EXPIRES_MAX_AGE__3M * 24 * 60 * 60;
    }

    setCookie(COOKIE_NAME__ACCESS_TOKEN, response.token, options);

    if (response.refresh_token) {
      setCookie(COOKIE_NAME__REFRESH_TOKEN, response.refresh_token, {
        maxAge: COOKIE_NAME__EXPIRES_MAX_AGE__3M * 24 * 60 * 60,
      });
    }
  }

  return response?.token;
};

export const dispatchRequireAuth = (isTokenExpired = false) => {
  // const logout = authStore.getState().logout;
  // logout(isTokenExpired);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (typeof window !== 'undefined' && (window as any).CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const event = new (window as any).CustomEvent(REQUIRE_AUTH_EVENT, {
      detail: { isTokenExpired },
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
    window.dispatchEvent(event);
  }
};

installAxiosInterceptors(httpClient);

export { httpClient };

export type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
};
